import { AxiosError } from 'axios';
import { ApiError, ApiErrorResponse, ErrorType, FormErrors } from '../models/ApiError';
import { captureException, addBreadcrumb } from './sentry';

/**
 * Configuration options for ErrorHandler
 */
export interface ErrorHandlerConfig {
  /**
   * Custom error messages for different error types
   */
  customMessages?: Partial<Record<ErrorType, string>>;
  
  /**
   * Whether to log errors to console
   */
  enableLogging?: boolean;
  
  /**
   * Custom logger function
   */
  logger?: (error: ApiError, context?: any) => void;
  
  /**
   * Field name mapping for form errors (backend field -> frontend field)
   */
  fieldMapping?: Record<string, string>;
}

/**
 * Centralized Error Handler for API errors
 * 
 * This class provides a complete error handling solution that can be
 * used across multiple projects. It handles error conversion, user-friendly
 * messages, form validation errors, and more.
 * 
 * @example
 * ```typescript
 * const errorHandler = new ErrorHandler({
 *   enableLogging: true,
 *   customMessages: {
 *     [ErrorType.AUTHENTICATION_ERROR]: 'Please log in again'
 *   }
 * });
 * 
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const apiError = errorHandler.handle(error);
 *   alert(errorHandler.getUserFriendlyMessage(apiError));
 * }
 * ```
 */
export class ErrorHandler {
  private config: ErrorHandlerConfig;

  constructor(config: ErrorHandlerConfig = {}) {
    this.config = {
      enableLogging: false,
      fieldMapping: {},
      ...config,
    };
  }

  /**
   * Main error handling method - converts any error to ApiError
   */
  handle(error: any): ApiError {
    let apiError: ApiError;

    if (ApiError.isApiError(error)) {
      apiError = error;
    } else if (this.isAxiosError(error)) {
      apiError = this.convertAxiosErrorToApiError(error);
    } else {
      apiError = ApiError.fromUnknownError(error);
    }

    if (this.config.enableLogging) {
      this.logError(apiError);
    }

    if (this.config.logger) {
      this.config.logger(apiError);
    }

    // Send to Sentry (only for non-validation errors and errors we care about)
    this.sendToSentry(apiError, error);

    return apiError;
  }

  /**
   * Convert Axios error to ApiError
   */
  private convertAxiosErrorToApiError(error: AxiosError): ApiError {
    // Check if response has the standardized error format
    if (error.response?.data && typeof error.response.data === 'object') {
      const responseData = error.response.data as any;

      // Check if it matches our ApiErrorResponse format
      if (responseData.error && responseData.error.type) {
        const apiErrorResponse: ApiErrorResponse = {
          message: responseData.message || error.message,
          timestamp: responseData.timestamp || new Date().toISOString(),
          requestId: responseData.requestId || '',
          error: {
            type: responseData.error.type as ErrorType,
            details: responseData.error.details,
          },
        };
        return new ApiError(apiErrorResponse, error.response.status);
      }
    }

    // Fallback: create ApiError from status code
    const statusCode = error.response?.status || 500;
    const errorType = this.getErrorTypeFromStatus(statusCode);
    const message = this.getMessageFromAxiosError(error);

    const apiErrorResponse: ApiErrorResponse = {
      message,
      timestamp: new Date().toISOString(),
      requestId: '',
      error: {
        type: errorType,
        details: {
          originalError: error.response?.data,
          statusCode,
        },
      },
    };

    return new ApiError(apiErrorResponse, statusCode);
  }

  /**
   * Check if error is an Axios error
   */
  private isAxiosError(error: any): error is AxiosError {
    return error && error.isAxiosError === true;
  }

  /**
   * Get error type based on HTTP status code
   */
  private getErrorTypeFromStatus(statusCode: number): ErrorType {
    switch (statusCode) {
      case 400:
        return ErrorType.VALIDATION_ERROR;
      case 401:
        return ErrorType.AUTHENTICATION_ERROR;
      case 403:
        return ErrorType.AUTHORIZATION_ERROR;
      case 404:
        return ErrorType.NOT_FOUND_ERROR;
      case 409:
        return ErrorType.CONFLICT_ERROR;
      case 429:
        return ErrorType.RATE_LIMIT_ERROR;
      case 502:
      case 503:
        return ErrorType.EXTERNAL_SERVICE_ERROR;
      default:
        return ErrorType.INTERNAL_ERROR;
    }
  }

  /**
   * Extract message from Axios error
   */
  private getMessageFromAxiosError(error: AxiosError): string {
    if (error.response?.data) {
      const data = error.response.data as any;
      if (data.message) return data.message;
    }

    if (error.message) return error.message;

    return 'An unexpected error occurred';
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error: ApiError): string {
    // Check for custom message first
    if (this.config.customMessages && this.config.customMessages[error.type]) {
      return this.config.customMessages[error.type]!;
    }

    switch (error.type) {
      case ErrorType.VALIDATION_ERROR:
        return this.handleValidationError(error);
      case ErrorType.AUTHENTICATION_ERROR:
        return this.handleAuthenticationError(error);
      case ErrorType.AUTHORIZATION_ERROR:
        return this.handleAuthorizationError(error);
      case ErrorType.NOT_FOUND_ERROR:
        return this.handleNotFoundError(error);
      case ErrorType.CONFLICT_ERROR:
        return this.handleConflictError(error);
      case ErrorType.RATE_LIMIT_ERROR:
        return this.handleRateLimitError(error);
      case ErrorType.EXTERNAL_SERVICE_ERROR:
        return this.handleExternalServiceError(error);
      case ErrorType.INTERNAL_ERROR:
        return this.handleInternalError(error);
      case ErrorType.NETWORK_ERROR:
        return this.handleNetworkError(error);
      default:
        return error.message || 'An unknown error occurred. Please try again.';
    }
  }

  /**
   * Handle validation errors
   */
  private handleValidationError(error: ApiError): string {
    const { details } = error;

    if (details?.missingFields && Array.isArray(details.missingFields)) {
      return `Missing required fields: ${details.missingFields.join(', ')}`;
    }

    if (details?.field && details?.value) {
      return `Invalid ${details.field}: ${details.value}`;
    }

    if (details?.field) {
      return `Invalid ${details.field}`;
    }

    // Check for specific validation messages
    if (error.message.includes('Invalid email')) {
      return 'Please enter a valid email address';
    }

    if (error.message.includes('Invalid phone')) {
      return 'Please enter a valid phone number';
    }

    return error.message || 'Invalid input. Please check your data and try again.';
  }

  /**
   * Handle authentication errors
   */
  private handleAuthenticationError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('missing authorization header') || message.includes('missing token')) {
      return 'Please log in to continue';
    }

    if (message.includes('invalid credentials') || message.includes('incorrect username or password')) {
      return 'Invalid username or password';
    }

    if (message.includes('expired') || message.includes('token expired')) {
      return 'Session expired. Please log in again';
    }

    if (message.includes('invalid token') || message.includes('malformed token')) {
      return 'Invalid authentication. Please log in again';
    }

    return 'Authentication failed. Please try logging in again';
  }

  /**
   * Handle authorization errors
   */
  private handleAuthorizationError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('insufficient permissions')) {
      return 'You do not have sufficient permissions to perform this action';
    }

    if (message.includes('access denied')) {
      return 'Access denied. Please contact your administrator';
    }

    return 'You do not have permission to perform this action';
  }

  /**
   * Handle not found errors
   */
  private handleNotFoundError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('user')) {
      return 'User not found';
    }

    if (message.includes('item')) {
      return 'Item not found';
    }

    if (message.includes('generic item')) {
      return 'Generic item not found';
    }

    if (message.includes('resource')) {
      return 'The requested resource was not found';
    }

    return error.message || 'The requested item was not found';
  }

  /**
   * Handle conflict errors
   */
  private handleConflictError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('username') && message.includes('already exists')) {
      return 'This username is already taken. Please choose another.';
    }

    if (message.includes('email') && message.includes('already exists')) {
      return 'This email is already registered. Please use a different email.';
    }

    if (message.includes('already exists')) {
      return 'This resource already exists. Please use different values.';
    }

    if (message.includes('duplicate')) {
      return 'A duplicate entry was detected. Please check your data.';
    }

    return error.message || 'A conflict occurred. The resource may already exist.';
  }

  /**
   * Handle rate limit errors
   */
  private handleRateLimitError(error: ApiError): string {
    const { details } = error;

    if (details?.retryAfter) {
      return `Too many requests. Please wait ${details.retryAfter} seconds and try again.`;
    }

    return 'Too many requests. Please wait a moment and try again.';
  }

  /**
   * Handle external service errors
   */
  private handleExternalServiceError(error: ApiError): string {
    return 'Service temporarily unavailable. Please try again in a few moments.';
  }

  /**
   * Handle internal server errors
   */
  private handleInternalError(error: ApiError): string {
    return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }

  /**
   * Handle network errors
   */
  private handleNetworkError(error: ApiError): string {
    return 'Network error. Please check your internet connection and try again.';
  }

  /**
   * Extract field-specific errors from validation errors
   */
  extractFieldErrors(error: ApiError): FormErrors {
    const formErrors: FormErrors = {};

    if (error.type !== ErrorType.VALIDATION_ERROR || !error.details) {
      return formErrors;
    }

    const { details } = error;

    // Handle missing fields
    if (details.missingFields && Array.isArray(details.missingFields)) {
      details.missingFields.forEach((field: string) => {
        const frontendField = this.mapFieldName(field);
        formErrors[frontendField] = `${this.formatFieldName(field)} is required`;
      });
    }

    // Handle specific field errors
    if (details.field && details.value !== undefined) {
      const frontendField = this.mapFieldName(details.field);
      formErrors[frontendField] = this.getFieldErrorMessage(details.field, details.value, details);
    }

    // Handle field-specific errors from message
    const message = error.message.toLowerCase();

    if (message.includes('invalid email')) {
      formErrors.email = 'Please enter a valid email address';
    }

    if (message.includes('invalid phone number')) {
      const phoneField = this.mapFieldName('phone_number');
      formErrors[phoneField] = 'Please enter a valid phone number';
    }

    if (message.includes('password') && message.includes('requirements')) {
      formErrors.password = 'Password does not meet requirements';
    }

    if (message.includes('username') && message.includes('already exists')) {
      formErrors.username = 'This username is already taken';
    }

    if (message.includes('email') && message.includes('already exists')) {
      formErrors.email = 'This email is already registered';
    }

    return formErrors;
  }

  /**
   * Get error message for a specific field
   */
  private getFieldErrorMessage(field: string, value: any, details: any): string {
    const fieldName = this.formatFieldName(field);

    // Check for expected format in details
    if (details.expectedFormat) {
      return `Invalid ${fieldName}. Expected format: ${details.expectedFormat}`;
    }

    // Field-specific messages
    switch (field) {
      case 'email':
        return 'Please enter a valid email address';
      case 'phone_number':
      case 'phoneNumber':
        return 'Please enter a valid phone number';
      case 'password':
        return 'Password does not meet requirements';
      case 'username':
        return 'Invalid username';
      case 'name':
        return 'Invalid name';
      case 'description':
        return 'Invalid description';
      case 'checkIntervalMonths':
        return 'Check interval (months) must be a positive number';
      case 'responsible':
        return 'Responsible field is required';
      case 'location':
        return 'Invalid location';
      default:
        return `Invalid ${fieldName}`;
    }
  }

  /**
   * Map backend field name to frontend field name
   */
  private mapFieldName(field: string): string {
    if (this.config.fieldMapping && this.config.fieldMapping[field]) {
      return this.config.fieldMapping[field];
    }

    // Default mappings
    const defaultMappings: Record<string, string> = {
      'custom:role': 'role',
      'custom:app': 'app',
      userName: 'username',
    };

    return defaultMappings[field] || field;
  }

  /**
   * Format field name for display
   */
  private formatFieldName(field: string): string {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  }

  /**
   * Check if error contains form validation errors
   */
  hasFormErrors(error: ApiError): boolean {
    return error.type === ErrorType.VALIDATION_ERROR && !!error.details;
  }

  /**
   * Get general form error message (non-field-specific)
   */
  getGeneralFormError(error: ApiError): string | null {
    if (error.type === ErrorType.VALIDATION_ERROR) {
      const fieldErrors = this.extractFieldErrors(error);
      // If we have field-specific errors, don't show a general message
      if (Object.keys(fieldErrors).length > 0) {
        return null;
      }
      return error.message;
    }
    return null;
  }

  /**
   * Convert form errors to a list of strings
   */
  formErrorsToList(errors: FormErrors): string[] {
    return Object.entries(errors).map(([field, message]) => message);
  }

  /**
   * Determine if an error should trigger a logout
   */
  shouldLogout(error: ApiError): boolean {
    if (error.type === ErrorType.AUTHENTICATION_ERROR) {
      const message = error.message.toLowerCase();
      // Don't logout for invalid credentials on login attempt
      if (message.includes('invalid credentials') || message.includes('incorrect username')) {
        return false;
      }
      // Logout for expired or invalid tokens
      return message.includes('expired') || message.includes('invalid token');
    }
    return false;
  }

  /**
   * Determine if an error should be retried automatically
   */
  shouldRetry(error: ApiError): boolean {
    return (
      error.type === ErrorType.EXTERNAL_SERVICE_ERROR ||
      error.type === ErrorType.NETWORK_ERROR ||
      (error.type === ErrorType.INTERNAL_ERROR && error.statusCode === 503)
    );
  }

  /**
   * Send error to Sentry with appropriate context
   */
  private sendToSentry(apiError: ApiError, originalError?: any): void {
    // Don't send validation errors to Sentry (they're expected user input errors)
    if (apiError.type === ErrorType.VALIDATION_ERROR) {
      return;
    }

    // Don't send authentication errors for invalid credentials (expected user errors)
    if (apiError.type === ErrorType.AUTHENTICATION_ERROR) {
      const message = apiError.message.toLowerCase();
      if (message.includes('invalid credentials') || message.includes('incorrect username')) {
        return;
      }
    }

    // Add breadcrumb for all errors
    addBreadcrumb(
      `API Error: ${apiError.type}`,
      'error',
      this.getSentrySeverity(apiError.type),
      {
        message: apiError.message,
        statusCode: apiError.statusCode,
        requestId: apiError.requestId,
        errorType: apiError.type,
      }
    );

    // Create error for Sentry
    const sentryError = originalError instanceof Error 
      ? originalError 
      : new Error(apiError.message);

    // Set error name to include error type
    sentryError.name = `ApiError: ${apiError.type}`;

    // Capture with context
    captureException(sentryError, {
      tags: {
        errorType: apiError.type,
        statusCode: apiError.statusCode?.toString() || 'unknown',
        requestId: apiError.requestId || 'none',
      },
      extra: {
        apiError: {
          type: apiError.type,
          message: apiError.message,
          details: apiError.details,
          timestamp: apiError.timestamp,
          requestId: apiError.requestId,
          statusCode: apiError.statusCode,
        },
      },
      level: this.getSentrySeverity(apiError.type),
    });
  }

  /**
   * Get Sentry severity level based on error type
   */
  private getSentrySeverity(errorType: ErrorType): 'fatal' | 'error' | 'warning' | 'info' | 'debug' {
    switch (errorType) {
      case ErrorType.INTERNAL_ERROR:
      case ErrorType.EXTERNAL_SERVICE_ERROR:
        return 'error';
      case ErrorType.AUTHENTICATION_ERROR:
      case ErrorType.AUTHORIZATION_ERROR:
        return 'warning';
      case ErrorType.NETWORK_ERROR:
      case ErrorType.RATE_LIMIT_ERROR:
        return 'warning';
      case ErrorType.VALIDATION_ERROR:
        return 'info';
      default:
        return 'error';
    }
  }

  /**
   * Log error to console
   */
  private logError(error: ApiError, context?: any): void {
    console.group('🔴 API Error');
    console.error('Type:', error.type);
    console.error('Message:', error.message);
    console.error('Status Code:', error.statusCode);
    console.error('Details:', error.details);
    console.error('Request ID:', error.requestId);
    console.error('Timestamp:', error.timestamp);
    if (context) {
      console.error('Context:', context);
    }
    console.groupEnd();
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): ErrorHandlerConfig {
    return { ...this.config };
  }
}

/**
 * Default error handler instance
 * Can be imported and used directly
 */
export const defaultErrorHandler = new ErrorHandler({
  enableLogging: import.meta.env.MODE === 'development',
});

